"use client";

import type { RouterOutputs } from "@api/trpc/routers/_app";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { Separator } from "@/components/ui/separator";
import { QuestionStatusCard } from "@/features/quiz/components/question-status-card";
import { useImagePreviewStore } from "@/lib/hooks/use-preview-image";
import { cn, determineQuestionStatus } from "@/lib/utils";
import { DeleteQuestionModal } from "./delete-question-modal";
import { EditQuestionModal } from "./edit-question-modal";
import QuestionActionDropDown from "./question-action-dropdown";

type QuestionCardProps = {
  className?: string;
  question: RouterOutputs["questions"]["getWithFileNames"]["questions"][0];
  showActions?: boolean;
  index?: number;
  isPolling?: boolean;
};

export const QuestionCard = ({
  question,
  className,
  showActions,
  index,
  isPolling,
}: QuestionCardProps) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const questionTextRef = useRef<HTMLHeadingElement>(null);
  const answerTextRef = useRef<HTMLHeadingElement>(null);
  const questionStatus = determineQuestionStatus(
    question.consecutiveCorrectAnswers ?? 0,
  );
  const openImagePreview = useImagePreviewStore((state) => state.open);

  const checkOverflow = useCallback(() => {
    if (isExpanded) return;
    const qEl = questionTextRef.current;
    const aEl = answerTextRef.current;
    const qOverflows = qEl ? qEl.scrollHeight > qEl.clientHeight : false;
    const aOverflows = aEl ? aEl.scrollHeight > aEl.clientHeight : false;
    setIsOverflowing(qOverflows || aOverflows);
  }, [isExpanded]);

  useEffect(() => {
    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    if (questionTextRef.current) observer.observe(questionTextRef.current);
    if (answerTextRef.current) observer.observe(answerTextRef.current);
    return () => observer.disconnect();
  }, [checkOverflow]);

  return (
    <>
      <div
        id={`question-${question.questions.id}`}
        onClick={
          isOverflowing || isExpanded
            ? () => setIsExpanded((prev) => !prev)
            : undefined
        }
        className={cn(
          "relative w-full border bg-muted/40 p-4 rounded-2xl transition-[height] duration-200 ease-in-out",
          !isExpanded && "md:h-[180px] overflow-hidden",
          isExpanded && "md:h-fit",
          (isOverflowing || isExpanded) && "cursor-pointer",
          className,
        )}
      >
        <div
          className="hidden md:flex w-full justify-end items-center gap-2 absolute right-4 top-4"
          onClick={(e) => e.stopPropagation()}
        >
          {question.consecutiveCorrectAnswers !== null && (
            <QuestionStatusCard questionStatus={questionStatus} />
          )}
          {showActions && (
            <QuestionActionDropDown
              setIsDeleteOpen={setIsDeleteOpen}
              setIsEditOpen={setIsEditOpen}
            />
          )}
        </div>
        <div className="flex flex-col md:flex-row w-full h-full gap-6 flex-1 self-stretch md:pr-36">
          {/* Question section */}
          <div className="w-full md:w-[35%] flex items-center gap-6 h-full min-h-0">
            <h2
              ref={questionTextRef}
              className={cn(
                "text-sm font-normal leading-5 whitespace-pre-line",
                !isExpanded && "line-clamp-6",
              )}
            >
              {question.questions.question}
            </h2>
            {question.questions.questionImageUrl && (
              <Image
                onClick={(e) => {
                  e.stopPropagation();
                  openImagePreview(
                    question.questions.questionImageUrl!,
                    "Question Image",
                  );
                }}
                src={question.questions.questionImageUrl}
                alt="Question Image"
                className="rounded-md max-w-full object-contain cursor-zoom-in"
                width={120}
                height={80}
              />
            )}
          </div>
          {/* Separator - horizontal for mobile, vertical for desktop */}
          <Separator
            className="md:h-full h-[1px] md:w-[1px]"
            orientation={window.innerWidth >= 768 ? "vertical" : "horizontal"}
          />
          {/* Answer section */}
          <div className="w-full md:w-[65%] flex items-center gap-6 h-full min-h-0">
            <h2
              ref={answerTextRef}
              className={cn(
                "text-sm font-normal leading-5 whitespace-pre-line",
                !isExpanded && "line-clamp-6",
              )}
            >
              {question.questions.answer}
            </h2>
            {question.questions.answerImageUrl && (
              <Image
                onClick={(e) => {
                  e.stopPropagation();
                  openImagePreview(
                    question.questions.answerImageUrl!,
                    "Answer Image",
                  );
                }}
                src={question.questions.answerImageUrl}
                alt="Answer Image"
                className="rounded-md max-w-full object-contain cursor-zoom-in"
                width={100}
                height={64}
              />
            )}
          </div>
        </div>
      </div>
      <EditQuestionModal
        question={question}
        isOpen={isEditOpen}
        setIsOpen={setIsEditOpen}
      />
      <DeleteQuestionModal
        questionId={question.questions.id}
        studySetId={question.questions.studySetId}
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
      />
    </>
  );
};
